import React, { useRef, useEffect } from "react";

const nodeRadius = 40;

const MIN_FLOW_THRESHOLD = 0.05;
const GENSETS_ACTIVE_THRESHOLD = 0.5;

const DataFlowAnimation = ({ load, solar, genset, grid, icons = {} }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(0);
  const nodeImagesRef = useRef({});

  // Load the node images (from public/images/dashboard) once.
  useEffect(() => {
    const sources = {
      solar: "/images/dashboard/solar.svg",
      grid: "/images/dashboard/tower.svg",
      genset: "/images/dashboard/generator.svg",
      load: "/images/dashboard/transformer.svg",
    };
    const imgs = {};
    Object.entries(sources).forEach(([key, src]) => {
      const img = new Image();
      img.src = src;
      imgs[key] = img;
    });
    nodeImagesRef.current = imgs;
  }, []);

  const parseValue = (val) => {
    // Match Flutter _parseKw: remove "kw", "+", ",", trim, then parse
    if (val == null) return 0;
    const s = val
      .toString()
      .toLowerCase()
      .replaceAll("kw", "")
      .trim()
      .replaceAll("+", "")
      .replaceAll(",", "");
    const num = parseFloat(s);
    return Number.isNaN(num) ? 0 : num;
  };

  const numeric = {
    load: parseValue(load),
    solar: parseValue(solar),
    genset: parseValue(genset),
    grid: parseValue(grid),
  };

  // Replicate DataFlowController logic
  const hasFlow = (value) => Math.abs(value) >= MIN_FLOW_THRESHOLD;

  // Backend convention: grid POSITIVE = importing, NEGATIVE = exporting.
  const flowGridToLoad = numeric.grid > MIN_FLOW_THRESHOLD; // import → Grid feeds Load
  const flowSolarToGrid = numeric.grid < -MIN_FLOW_THRESHOLD; // export → Solar feeds Grid
  const flowSolarToLoad = hasFlow(numeric.solar);

  // In Flutter they check for the presence of the "genset" key;
  // here we approximate by checking if a value was actually provided.
  const gensetPresent = genset !== undefined && genset !== null;
  const gensetActive =
    gensetPresent && Math.abs(numeric.genset) >= GENSETS_ACTIVE_THRESHOLD;
  const flowGensetToLoad = gensetActive;

  const anyFlow =
    flowGridToLoad || flowSolarToGrid || flowSolarToLoad || flowGensetToLoad;

  const drawNode = (ctx, x, y, color, label, value, iconImg) => {
    // Icon clipped inside the circle, tinted to the node (box) color.
    if (iconImg && iconImg.complete) {
      try {
        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, nodeRadius - 2, 0, 2 * Math.PI);
        ctx.clip();
        // Slight inset so the icon sits nicely inside the ring
        const pad = 10;
        ctx.drawImage(
          iconImg,
          x - nodeRadius + pad,
          y - nodeRadius + pad,
          (nodeRadius - pad) * 2,
          (nodeRadius - pad) * 2,
        );
        // Recolor the icon shape to the node color (keep icon silhouette)
        ctx.globalCompositeOperation = "source-in";
        ctx.fillStyle = color;
        ctx.fillRect(
          x - nodeRadius,
          y - nodeRadius,
          nodeRadius * 2,
          nodeRadius * 2,
        );
      } catch (e) {
        // ignore draw errors (e.g. SVG with no intrinsic size)
      } finally {
        ctx.restore();
      }
    }

    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(x, y, nodeRadius, 0, 2 * Math.PI);
    ctx.stroke();

    ctx.fillStyle = "#000";
    ctx.font = "14px Arial";
    ctx.textAlign = "center";
    ctx.fillText(label, x, y - nodeRadius - 10);

    // Display absolute value (no minus sign), like Flutter formatting
    ctx.fillStyle = color;
    const displayValue = Math.abs(value).toFixed(3);
    ctx.fillText(`${displayValue} kW`, x, y + nodeRadius + 15);
  };

  const drawFlow = (ctx, points, color, phase, animate) => {
    if (!points || points.length < 2) return;

    // Compute segment lengths and total length
    const segLens = [];
    let totalLen = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i];
      const b = points[i + 1];
      const len = Math.hypot(b.x - a.x, b.y - a.y);
      segLens.push(len);
      totalLen += len;
    }
    if (totalLen === 0) return;

    const segLen = Math.max(totalLen * 0.06, 8);

    const getPointAt = (dist) => {
      let d = dist;
      for (let i = 0; i < segLens.length; i++) {
        if (d <= segLens[i]) {
          const t = d / segLens[i];
          const a = points[i];
          const b = points[i + 1];
          return {
            x: a.x + (b.x - a.x) * t,
            y: a.y + (b.y - a.y) * t,
          };
        }
        d -= segLens[i];
      }
      return points[points.length - 1];
    };

    // Base path (always drawn)
    ctx.strokeStyle = color;
    ctx.lineWidth = 5;
    ctx.globalAlpha = 0.22;
    ctx.beginPath();
    ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.stroke();

    if (!animate) {
      ctx.globalAlpha = 1.0;
      return;
    }

    ctx.globalAlpha = 1.0;

    const drawSegment = (s, e) => {
      const startPt = getPointAt(s);
      const endPt = getPointAt(e);
      ctx.beginPath();
      ctx.moveTo(startPt.x, startPt.y);
      ctx.lineTo(endPt.x, endPt.y);
      ctx.stroke();
    };

    // Three moving segments along the path, similar to Flutter loop
    for (let i = 0; i < 3; i++) {
      const localPhase = (phase + i / 3) % 1.0;
      const flowStart = localPhase * totalLen;
      let flowEnd = flowStart + segLen;

      if (flowEnd > totalLen) {
        drawSegment(flowStart, totalLen);
        drawSegment(0, flowEnd - totalLen);
      } else {
        drawSegment(flowStart, flowEnd);
      }
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let frameId;

    const render = () => {
      const width = canvas.width;
      const height = canvas.height;

      ctx.clearRect(0, 0, width, height);

      const centerX = width / 2;
      const centerY = height / 2;

      const positions = {
        solar: { x: centerX - width * 0.3, y: centerY },
        grid: { x: centerX, y: centerY - height * 0.3 },
        genset: { x: centerX + width * 0.3, y: centerY },
        load: { x: centerX, y: centerY + height * 0.3 },
      };

      // Junctions left/right of the center so solar and genset flows are symmetric
      const solarJunction = { x: centerX - width * 0.15, y: centerY };
      const gensetJunction = { x: centerX + width * 0.15, y: centerY };

      // Nodes
      const imgs = nodeImagesRef.current || {};
      drawNode(
        ctx,
        positions.solar.x,
        positions.solar.y,
        "#2ecc71",
        "Solar",
        numeric.solar,
        imgs.solar
      );
      drawNode(
        ctx,
        positions.grid.x,
        positions.grid.y,
        "#f39c12",
        "Grid",
        numeric.grid,
        imgs.grid
      );
      drawNode(
        ctx,
        positions.genset.x,
        positions.genset.y,
        gensetPresent ? "#e74c3c" : "#bdc3c7",
        "Genset",
        numeric.genset,
        imgs.genset
      );
      drawNode(
        ctx,
        positions.load.x,
        positions.load.y,
        "#3498db",
        "Load",
        numeric.load,
        imgs.load
      );

      // Flows (directions and on/off logic from controller)
      const phase = (animationRef.current % 100) / 100;

      const gridImportColor = "#f39c12"; // same as Grid node (orange)
      const gridExportColor = "#2e7d32"; // green

      // All flows start and end at the CENTER EDGE of nodes:
      // - center of bottom/top/left/right, never at diagonal points.

      // grid → load: straight vertical from bottom center of Grid to top center of Load
      const gridToLoadPoints = [
        { x: positions.grid.x, y: positions.grid.y + nodeRadius }, // bottom edge center of grid
        { x: positions.load.x, y: positions.load.y - nodeRadius }, // top edge center of load
      ];
      drawFlow(
        ctx,
        gridToLoadPoints,
        gridImportColor,
        phase,
        flowGridToLoad
      );

      // solar → grid: right edge center of Solar → left junction (horizontal) →
      //               up/down (vertical) → left edge center of Grid
      const solarToGridPoints = [
        { x: positions.solar.x + nodeRadius, y: positions.solar.y }, // right edge center solar
        { x: solarJunction.x, y: positions.solar.y },                // horizontal to junction
        solarJunction,                                               // center junction
        { x: solarJunction.x, y: positions.grid.y },                 // vertical align with grid center
        { x: positions.grid.x - nodeRadius, y: positions.grid.y },   // left edge center of grid
      ];
      drawFlow(
        ctx,
        solarToGridPoints,
        gridExportColor,
        phase,
        flowSolarToGrid
      );

      // solar → load: same start & junction, but go down and then into LEFT edge center of Load
      const solarToLoadPoints = [
        { x: positions.solar.x + nodeRadius, y: positions.solar.y }, // right edge center solar
        { x: solarJunction.x, y: positions.solar.y },
        solarJunction,
        { x: solarJunction.x, y: positions.load.y },                 // align vertically with load center
        { x: positions.load.x - nodeRadius, y: positions.load.y },   // left edge center load
      ];
      drawFlow(
        ctx,
        solarToLoadPoints,
        "#2ecc71",
        (phase + 0.33) % 1,
        flowSolarToLoad
      );
      if (gensetPresent) {
        // genset → load: mirror of solar → load on the right side
        // from LEFT edge center of Genset to RIGHT edge center of Load
        const gensetToLoadPoints = [
          { x: positions.genset.x - nodeRadius, y: positions.genset.y }, // left edge center genset
          { x: gensetJunction.x, y: positions.genset.y },                // horizontal to junction
          gensetJunction,                                                // center junction
          { x: gensetJunction.x, y: positions.load.y },                  // vertical align with load center
          { x: positions.load.x + nodeRadius, y: positions.load.y },     // right edge center load
        ];
        drawFlow(
          ctx,
          gensetToLoadPoints,
          "#e74c3c",
          (phase + 0.66) % 1,
          flowGensetToLoad
        );
      }

      if (anyFlow) {
        animationRef.current += 2;
      }

      frameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
    // We intentionally don't include all derived booleans/builders
    // in the dependency array to avoid restarting the animation loop
    // on every frame; numeric values changing are enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numeric.load, numeric.solar, numeric.genset, numeric.grid, anyFlow]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <canvas
        ref={canvasRef}
        width={600}
        height={400}
        style={{ width: "100%", height: "100%" }}
      />

      {/* Colored SCADA-style icons, tinted to match node colors */}
      {icons.solarIcon && (
        <div
          aria-label="Solar"
          style={iconMaskStyle(20, 50, "#2ecc71", icons.solarIcon)}
        />
      )}
      {icons.gridIcon && (
        <div
          aria-label="Grid"
          style={iconMaskStyle(50, 20, "#f39c12", icons.gridIcon)}
        />
      )}
      {icons.gensetIcon && (
        <div
          aria-label="Genset"
          style={iconMaskStyle(
            80,
            50,
            gensetPresent ? "#e74c3c" : "#bdc3c7",
            icons.gensetIcon
          )}
        />
      )}
      {icons.loadIcon && (
        <div
          aria-label="Load"
          style={iconMaskStyle(50, 80, "#3498db", icons.loadIcon)}
        />
      )}
    </div>
  );
};

const iconMaskStyle = (leftPercent, topPercent, color, src) => ({
  position: "absolute",
  left: `${leftPercent}%`,
  top: `${topPercent}%`,
  transform: "translate(-50%, -50%)",
  width: 40,
  height: 40,
  backgroundColor: color,
  WebkitMaskImage: `url(${src})`,
  maskImage: `url(${src})`,
  WebkitMaskRepeat: "no-repeat",
  maskRepeat: "no-repeat",
  WebkitMaskPosition: "center",
  maskPosition: "center",
  WebkitMaskSize: "contain",
  maskSize: "contain",
});

export default DataFlowAnimation;
